import React, { useState, useEffect } from 'react';
import { Container, Grid, Paper, Typography, Button, Box, Divider, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import styles from "./myprofile/myprofile.module.css";
import ProfileMenu from '../Components/profilemenu/ProfileMenu';
import VendorProfileMenu from '../../VendorDash/Components/vendorprofilemenu/VendorProfileMenu';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { format, isToday, isYesterday, formatDistanceToNow } from "date-fns";
import { markAsRead, getNotification, getNotificationCount, markAllAsRead, updateNotificationStatus, updateAllNotificationMarkAsRead } from '../../../Website/redux/notification';
import api from '../../../Website/utils/api';

const NotificationCard = styled(Paper)(({ theme, isread }) => ({
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: isread === 'true' ? '#e2e8f0' : 'rgba(81, 45, 168, 0.15)',
    background: isread === 'true' ? '#ffffff' : 'rgba(81, 45, 168, 0.02)',
    boxShadow: isread === 'true' ? 'none' : '0 4px 12px rgba(81, 45, 168, 0.03)',
    borderLeft: `5px solid ${isread === 'true' ? '#cbd5e1' : '#512DA8'}`,
    display: 'flex',
    gap: '20px',
    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
        borderColor: '#512DA8',
    }
}));

const IconContainer = styled(Box)(({ bgcolor }) => ({
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bgcolor,
    flexShrink: 0
}));

const getIconProps = (type) => {
    const cleanType = (type || '').toLowerCase();
    if (cleanType.includes('quota') || cleanType.includes('reply') || cleanType.includes('enquiry')) {
        return {
            bgcolor: 'rgba(81, 45, 168, 0.08)',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#512DA8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
            )
        };
    } else if (cleanType.includes('order') || cleanType.includes('cart')) {
        return {
            bgcolor: 'rgba(16, 185, 129, 0.08)',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
            )
        };
    } else if (cleanType.includes('login') || cleanType.includes('security') || cleanType.includes('registration')) {
        return {
            bgcolor: 'rgba(245, 158, 11, 0.08)',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
            )
        };
    } else {
        return {
            bgcolor: 'rgba(59, 130, 246, 0.08)',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
            )
        };
    }
};

const UserNotifications = () => {
    const nav = useNavigate();
    const dispatch = useDispatch();

    const [showAll, setShowAll] = useState(true);
    const [activeTab, setActiveTab] = useState('All');
    const user = useSelector((state) => state.customer);
    const userId = user?.customer?._id;
    const isVendor = user?.customer?.userType === 'VENDOR';
    const { notificationData, notificationCountData, isRefresh } = useSelector((state) => state.notification);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const fetchNotification = async () => {
            try {
                if (userId) {
                    await dispatch(getNotification({ body: userId, showAll: showAll }));
                    setLoaded(true);
                }
            } catch (error) {
                console.error("error occured", error);
            }
        };
        fetchNotification();
    }, [dispatch, isRefresh, userId, showAll]);

    // Notifications marked read on this visit by opening their tab. They drop out of the
    // tab badges right away but keep their unread highlight in the list until the next visit.
    const [seenNow, setSeenNow] = useState(() => new Set());

    const handleMark = async (id) => {
        dispatch(updateNotificationStatus(id));
        try {
            await dispatch(markAsRead(id));
        } catch (error) {
            console.log(error.message);
        }
    };

    const handleMarkAll = async () => {
        dispatch(updateAllNotificationMarkAsRead());
        try {
            if (userId) {
                await dispatch(markAllAsRead(userId));
            }
        } catch (error) {
            console.log(error.message);
        }
    };

    const handleViewDetails = (e, notification) => {
        if (e) e.stopPropagation();

        if (!notification.isRead) {
            handleMark(notification._id);
        }

        const type = (notification.notificationType || '').toLowerCase();
        const title = (notification.title || '').toLowerCase();
        const msg = (notification.message || '').toLowerCase();

        if (notification.targetUrl) {
            nav(notification.targetUrl);
            return;
        }
        if (notification.enquiryId) {
            nav(`/userEnquires/userEnquiryDetails/${notification.enquiryId}`);
            return;
        }
        if (notification.orderId) {
            nav(`/orderDetails/${notification.orderId}`);
            return;
        }

        if (isVendor) {
            nav(type.includes('order') ? '/receivedOrders' : '/recievedEnquiries');
            return;
        }

        if (type.includes('quota') || type.includes('reply') || type.includes('enquiry') || msg.includes('quote') || title.includes('quote') || msg.includes('bom') || title.includes('bom')) {
            nav('/userEnquires');
        } else if (type.includes('order') || title.includes('order') || msg.includes('order') || title.includes('po-') || msg.includes('po-')) {
            nav('/allOrders');
        } else if (type.includes('cart') || title.includes('cart') || msg.includes('cart')) {
            nav('/cart');
        } else if (type.includes('schedule') || title.includes('schedule') || msg.includes('schedule')) {
            nav('/userSchedulePO');
        } else if (type.includes('login') || type.includes('security') || type.includes('account') || type.includes('profile')) {
            nav('/myprofile');
        } else {
            nav('/userEnquires');
        }
    };

    const formattedDate = (date = new Date()) => {
        const dateFromMongoDB = new Date(date);

        if (isToday(dateFromMongoDB)) {
            return `Today, ${format(dateFromMongoDB, "hh:mm a")}`;
        } else if (isYesterday(dateFromMongoDB)) {
            return `Yesterday, ${format(dateFromMongoDB, "hh:mm a")}`;
        } else {
            const differenceInDays = Math.floor(
                (new Date() - dateFromMongoDB) / (1000 * 60 * 60 * 24)
            );
            if (differenceInDays < 7) {
                return `${formatDistanceToNow(dateFromMongoDB)} ago`;
            } else {
                const formatted = format(dateFromMongoDB, "dd MMM yyyy");
                const formattedTime = format(dateFromMongoDB, "hh:mm a");
                return `${formatted}, ${formattedTime}`;
            }
        }
    };

    // Filter Logic
    const rawData = notificationData || [];

    // Which module a notification belongs to. The notification type set by the
    // server is checked first; the title and message are the fallback for older ones.
    const notificationGroup = (item) => {
        const type = (item.notificationType || '').toLowerCase();
        const title = (item.title || '').toLowerCase();
        const msg = (item.message || '').toLowerCase();

        const pick = (text) => {
            if (text.includes('wizard')) return 'Wizard';
            if (text.includes('excess')) return 'Excess Inventory';
            if (text.includes('bom')) return 'BOM';
            if (text.includes('schedule') || text.includes('order-alert') || text.includes('reschedule')) return 'Scheduled';
            if (text.includes('sample')) return 'Sample Request';
            if (text.includes('cart')) return 'Cart Orders';
            if (text.includes('stock enquiry') || text.includes('availability') || text.includes('out-of-stock')) return 'Stock Enquiry';
            if (text.includes('registration') || text.includes('account') || text.includes('login') || text.includes('security') || text.includes('profile') || text.includes('welcome')) return 'Account';
            if (text.includes('order') || text.includes('po-') || text.includes('purchase')) return 'Orders';
            if (text.includes('quotation') || text.includes('enquiry')) return 'Enquiries';
            return null;
        };

        const fromType = pick(type);
        // Generic types (e.g. "vendor enquiry", "PO order") use the text to find the module when it names one
        if (!fromType || fromType === 'Enquiries' || fromType === 'Orders') {
            return pick(`${title} ${msg}`) || fromType || 'Other';
        }
        return fromType;
    };

    const MODULE_TABS = isVendor
        ? ['BOM', 'Wizard', 'Excess Inventory', 'Enquiries', 'Orders', 'Account']
        : ['BOM', 'Wizard', 'Excess Inventory', 'Scheduled', 'Cart Orders', 'Orders', 'Sample Request', 'Stock Enquiry', 'Account'];

    const matchesTab = (item, tab) => tab === 'All' || notificationGroup(item) === tab;

    const isUnread = (item) => item && !item.isRead && !seenNow.has(item._id);
    // Badge on each tab = unread notifications in it
    const countFor = (tab) => rawData.filter((item) => isUnread(item) && matchesTab(item, tab)).length;

    // Every module tab is shown with its count; a group outside the list only appears when it has notifications
    const extraTabs = [...new Set(rawData.filter(Boolean).map(notificationGroup))].filter((g) => !MODULE_TABS.includes(g));
    const notificationTabs = ['All', ...MODULE_TABS, ...extraTabs];

    const filteredData = rawData.filter((item) => matchesTab(item, activeTab));

    // Opening a module tab (not "All") marks its unread notifications as read, so its badge clears
    useEffect(() => {
        if (!loaded || activeTab === 'All') return;
        const ids = rawData.filter((item) => isUnread(item) && matchesTab(item, activeTab)).map((item) => item._id);
        if (!ids.length) return;
        setSeenNow((prev) => new Set([...prev, ...ids]));
        Promise.all(ids.map((id) => api.markAsRead(id).catch(() => null))).then(() => dispatch(getNotificationCount()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, loaded, notificationData]);

    // Summary cards built from the user's real notifications
    const countOf = (...groups) => rawData.filter((n) => n && groups.includes(notificationGroup(n))).length;
    const customerSummaryCards = [
        { title: 'Total', count: rawData.length, color: '#512DA8' },
        { title: 'Unread', count: rawData.filter(n => n && !n.isRead).length, color: '#F86624' },
        { title: 'Enquiries & Quotes', count: countOf('BOM', 'Wizard', 'Excess Inventory', 'Stock Enquiry', 'Enquiries'), color: '#2BB2FE' },
        { title: 'Orders', count: countOf('Scheduled', 'Cart Orders', 'Orders'), color: '#1A9882' },
    ];
    const vendorSummaryCards = [
        { title: 'Total', count: rawData.length, color: '#512DA8' },
        { title: 'Unread', count: rawData.filter(n => n && !n.isRead).length, color: '#F86624' },
        { title: 'BOM Requests', count: countOf('BOM'), color: '#2BB2FE' },
        { title: 'Wizard Requests', count: countOf('Wizard'), color: '#1A9882' },
    ];
    const summaryCards = isVendor ? vendorSummaryCards : customerSummaryCards;

    const unreadCount = filteredData.filter(n => !n.isRead).length;

    return (
        <div className={styles.myprofile}>
            <div className={styles.promenu}>
                {isVendor ? <VendorProfileMenu /> : <ProfileMenu />}
            </div>
            <div style={{ display: 'flex', gap: '24px', flexDirection: "column", flex: 1, minWidth: 0 }}>
                <Grid container spacing={2}>
                    {summaryCards.map((card) => (
                        <Grid item xs={12} sm={6} md={3} key={card.title}>
                            <Paper sx={{ padding: '16px 20px', borderRadius: '12px', border: '1px solid #E0E2E7', boxShadow: 'none', borderLeft: `4px solid ${card.color}` }}>
                                <Typography sx={{ fontSize: '14px', color: '#777980', fontWeight: 600 }}>{card.title}</Typography>
                                <Typography sx={{ fontSize: '28px', color: '#1D1F2C', fontWeight: 700, lineHeight: 1.3 }}>{card.count}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
                <div style={{ display: 'flex', gap: '24px', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Paper sx={{ width: '100%', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #f1f5f9' }}>
                            {/* Notification Header */}
                            <Box sx={{ padding: '24px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                                <Box>
                                    <Typography sx={{ fontSize: "20px", color: '#0f172a', fontWeight: '700', fontFamily: 'inherit' }}>
                                        Notifications Center
                                    </Typography>
                                    <Typography sx={{ fontSize: "13px", color: '#64748b', marginTop: '2px' }}>
                                        {isVendor
                                            ? 'Keep BOM, Wizard, excess inventory, and order updates organised in one place.'
                                            : 'Stay updated on your BOM requests, quotations, and order statuses.'}
                                    </Typography>
                                </Box>
                                {unreadCount > 0 && (
                                    <Button
                                        onClick={handleMarkAll}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            color: '#512DA8',
                                            borderColor: '#512DA8',
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            padding: '6px 14px',
                                            '&:hover': {
                                                background: 'rgba(81, 45, 168, 0.04)',
                                                borderColor: '#4527a0'
                                            }
                                        }}
                                    >
                                        Mark all as read
                                    </Button>
                                )}
                            </Box>

                            {/* Filters Tab Bar */}
                            <Box sx={{ display: 'flex', columnGap: '18px', rowGap: '20px', flexWrap: 'wrap', padding: '24px 30px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                {notificationTabs.map((tab) => (
                                    <Button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        sx={{
                                            position: 'relative',
                                            overflow: 'visible',
                                            textTransform: 'none',
                                            borderRadius: '20px',
                                            padding: '6px 16px',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            background: activeTab === tab ? '#512DA8' : '#f1f5f9',
                                            color: activeTab === tab ? '#ffffff' : '#64748b',
                                            '&:hover': {
                                                background: activeTab === tab ? '#4527a0' : '#e2e8f0',
                                            }
                                        }}
                                    >
                                        {tab}
                                        {countFor(tab) > 0 && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    position: 'absolute',
                                                    top: '-9px',
                                                    right: '-6px',
                                                    minWidth: '20px',
                                                    height: '20px',
                                                    padding: '0 6px',
                                                    borderRadius: '10px',
                                                    boxSizing: 'border-box',
                                                    border: '2px solid #ffffff',
                                                    background: '#512DA8',
                                                    color: '#ffffff',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                {countFor(tab) > 99 ? '99+' : countFor(tab)}
                                            </Box>
                                        )}
                                    </Button>
                                ))}
                            </Box>

                            {/* Notifications List Container */}
                            <Box sx={{ padding: '24px 30px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {filteredData.length === 0 ? (
                                    <Box sx={{ padding: '60px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                                                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                                            </svg>
                                        </div>
                                        <Typography sx={{ fontSize: '15px', color: '#64748b', fontWeight: '500' }}>
                                            No notifications found in this category
                                        </Typography>
                                    </Box>
                                ) : (
                                    filteredData.map((notification) => {
                                        const { bgcolor, icon } = getIconProps(notification.notificationType);
                                        return (
                                            <NotificationCard
                                                key={notification._id}
                                                isread={notification.isRead ? 'true' : 'false'}
                                                onClick={() => handleMark(notification._id)}
                                            >
                                                <IconContainer bgcolor={bgcolor}>
                                                    {icon}
                                                </IconContainer>
                                                
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                                                        <Typography sx={{ fontSize: '15px', fontWeight: notification.isRead ? '600' : '750', color: '#0f172a', fontFamily: 'inherit' }}>
                                                            {notification.title || (notification.notificationType === 'quota' || notification.notificationType === 'reply' ? 'Quote Update' : 'System Notification')}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>
                                                            {formattedDate(notification.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Typography sx={{ fontSize: '13.5px', color: '#475569', marginTop: '6px', lineHeight: '1.5', fontFamily: 'inherit' }}>
                                                        {notification.message}
                                                    </Typography>

                                                     <Box sx={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                                                        <Button
                                                            onClick={(e) => handleViewDetails(e, notification)}
                                                            size="small"
                                                            sx={{
                                                                color: '#ffffff',
                                                                background: '#0f172a',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                textTransform: 'none',
                                                                fontWeight: '600',
                                                                padding: '4px 12px',
                                                                '&:hover': {
                                                                    background: '#1e293b'
                                                                }
                                                            }}
                                                        >
                                                            View Details
                                                        </Button>
                                                        {!notification.isRead && (
                                                            <Button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMark(notification._id);
                                                                }}
                                                                size="small"
                                                                variant="text"
                                                                sx={{
                                                                    color: '#512DA8',
                                                                    fontSize: '11px',
                                                                    textTransform: 'none',
                                                                    fontWeight: '600',
                                                                    '&:hover': {
                                                                        background: 'rgba(81, 45, 168, 0.04)'
                                                                    }
                                                                }}
                                                            >
                                                                Mark as read
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </NotificationCard>
                                        );
                                    })
                                )}
                            </Box>
                        </Paper>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserNotifications;
